import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const CATEGORIES = [
{ id: 'all', label: 'All Assets', icon: 'Grid3X3' },
{ id: 'characters', label: 'Characters', icon: 'User' },
{ id: 'backgrounds', label: 'Backgrounds', icon: 'Image' },
{ id: 'objects', label: 'Game Objects', icon: 'Box' },
{ id: 'sfx', label: 'Sound Effects', icon: 'Volume2' },
{ id: 'music', label: 'Music Tracks', icon: 'Music' },
{ id: 'video-templates', label: 'Video Templates', icon: 'Video' },
{ id: 'animation-templates', label: 'Animation Templates', icon: 'Film' }];


const SORT_OPTIONS = [
{ value: 'popular', label: 'Most Popular' },
{ value: 'newest', label: 'Newest' },
{ value: 'price-low', label: 'Price: Low to High' },
{ value: 'price-high', label: 'Price: High to Low' },
{ value: 'rating', label: 'Top Rated' }];


const MARKETPLACE_ASSETS = [
{ id: 1, title: 'Neon Warrior Character Pack', category: 'characters', creator: 'PixelWizard', price: 4.99, rating: 4.8, sales: 234, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10b9595fa-1764666335649.png", thumbnailAlt: 'Colorful neon warrior character with glowing armor and weapons', tags: ['character', 'neon', 'warrior'], featured: true },
{ id: 2, title: 'Cyberpunk City Background', category: 'backgrounds', creator: 'AnimaStudio', price: 2.99, rating: 4.6, sales: 189, thumbnail: "https://images.unsplash.com/photo-1653142267767-a66c7cf17bc1", thumbnailAlt: 'Neon-lit cyberpunk city background with glowing buildings and streets', tags: ['background', 'cyberpunk', 'city'], featured: true },
{ id: 3, title: 'Epic Cinematic Music Pack', category: 'music', creator: 'BeatMaker99', price: 9.99, rating: 4.9, sales: 412, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1de1738d1-1772828156030.png", thumbnailAlt: 'Vibrant music visualization with colorful waveforms and neon lights', tags: ['music', 'cinematic', 'epic'], featured: true },
{ id: 4, title: 'Space Shooter Objects Pack', category: 'objects', creator: 'StarForge', price: 3.49, rating: 4.5, sales: 156, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_198229156-1773037703532.png", thumbnailAlt: 'Collection of colorful space shooter game objects including ships and asteroids', tags: ['objects', 'space', 'shooter'], featured: false },
{ id: 5, title: 'Fantasy RPG Sound Effects', category: 'sfx', creator: 'SoundCraft', price: 5.99, rating: 4.7, sales: 298, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1587abe39-1772562490175.png", thumbnailAlt: 'Fantasy RPG game scene with magical effects and glowing elements', tags: ['sfx', 'fantasy', 'rpg'], featured: false },
{ id: 6, title: 'YouTube Intro Template', category: 'video-templates', creator: 'CinematicAI', price: 7.99, rating: 4.4, sales: 87, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17cb3cb72-1764668915775.png", thumbnailAlt: 'Professional YouTube intro template with dynamic text and transitions', tags: ['template', 'youtube', 'intro'], featured: false },
{ id: 7, title: 'Dragon Character Animation', category: 'animation-templates', creator: 'AnimaStudio', price: 12.99, rating: 4.9, sales: 543, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1116f6ec2-1773037704608.png", thumbnailAlt: 'Majestic dragon character animation template with fire breath effects', tags: ['animation', 'dragon', 'character'], featured: true },
{ id: 8, title: 'Lo-fi Chill Music Pack', category: 'music', creator: 'BeatMaker99', price: 6.99, rating: 4.8, sales: 367, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1deb9cbaa-1773037706831.png", thumbnailAlt: 'Chill lo-fi music visualization with warm colors and relaxing atmosphere', tags: ['music', 'lofi', 'chill'], featured: false },
{ id: 9, title: 'Pixel Art Forest Background', category: 'backgrounds', creator: 'PixelWizard', price: 1.99, rating: 4.3, sales: 124, thumbnail: "https://images.unsplash.com/photo-1605268365764-7521ffa59d62", thumbnailAlt: 'Colorful pixel art forest background with trees and nature elements', tags: ['background', 'pixel', 'forest'], featured: false },
{ id: 10, title: 'Platformer Game Objects', category: 'objects', creator: 'GameForger', price: 2.49, rating: 4.6, sales: 201, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa68b8cd-1773037705021.png", thumbnailAlt: 'Colorful platformer game objects including coins, platforms and power-ups', tags: ['objects', 'platformer', 'game'], featured: false },
{ id: 11, title: 'TikTok Short Template Pack', category: 'video-templates', creator: 'CinematicAI', price: 4.99, rating: 4.5, sales: 178, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1faa90a64-1764760323805.png", thumbnailAlt: 'Dynamic TikTok short video template with trendy transitions and effects', tags: ['template', 'tiktok', 'short'], featured: false },
{ id: 12, title: 'Afrobeat Rhythm Pack', category: 'music', creator: 'AfroSound', price: 8.99, rating: 4.7, sales: 289, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16914e8e9-1768057098701.png", thumbnailAlt: 'Vibrant Afrobeat music visualization with colorful African-inspired patterns', tags: ['music', 'afrobeat', 'rhythm'], featured: false }];


const CATEGORY_COLORS = {
  characters: 'var(--color-primary)',
  backgrounds: '#10B981',
  objects: '#F59E0B',
  sfx: '#06B6D4',
  music: '#F43F5E',
  'video-templates': '#8B5CF6',
  'animation-templates': 'var(--color-accent)'
};

function AssetCard({ asset, onBuy, onPreview }) {
  const [purchased, setPurchased] = useState(false);
  const [wishlist, setWishlist] = useState(false);
  const catColor = CATEGORY_COLORS?.[asset?.category] || 'var(--color-primary)';
  const cat = CATEGORIES?.find((c) => c?.id === asset?.category);

  return (
    <div
      className="rounded-2xl overflow-hidden group transition-all duration-200 hover:shadow-lg"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}>
      
      {/* Thumbnail */}
      <div className="relative h-40 overflow-hidden">
        <Image src={asset?.thumbnail} alt={asset?.thumbnailAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        {asset?.featured &&
        <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: '#F59E0B', color: 'white', fontFamily: 'var(--font-caption)' }}>⭐ Featured</span>
        }
        <span className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: catColor, color: 'white', fontFamily: 'var(--font-caption)' }}>
          {cat?.label || asset?.category}
        </span>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <button
            onClick={() => onPreview?.(asset)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
            style={{ background: 'rgba(255,255,255,0.9)', color: '#1a1a1a', fontFamily: 'var(--font-caption)' }}>
            
            <Icon name="Eye" size={12} color="#1a1a1a" />
            Preview
          </button>
        </div>
        <button
          onClick={() => setWishlist((v) => !v)}
          className="absolute bottom-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all"
          style={{ background: wishlist ? 'rgba(244,63,94,0.9)' : 'rgba(0,0,0,0.5)' }}>
          
          <Icon name="Heart" size={13} color="white" fill={wishlist ? 'white' : 'none'} />
        </button>
      </div>
      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-semibold mb-0.5 truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{asset?.title}</h3>
        <p className="text-xs mb-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>by {asset?.creator}</p>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <Icon name="Star" size={11} color="#F59E0B" fill="#F59E0B" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{asset?.rating}</span>
            <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>({asset?.sales})</span>
          </div>
          <span className="text-base font-bold" style={{ color: catColor, fontFamily: 'var(--font-data)' }}>${asset?.price}</span>
        </div>
        <button
          onClick={() => {setPurchased(true);onBuy?.(asset);}}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90"
          style={{
            background: purchased ? 'var(--color-success)' : catColor,
            color: 'white',
            fontFamily: 'var(--font-heading)'
          }}>
          
          <Icon name={purchased ? 'CheckCircle2' : 'ShoppingCart'} size={13} color="white" />
          {purchased ? 'Purchased!' : `Buy for $${asset?.price}`}
        </button>
      </div>
    </div>);

}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [sort, setSort] = useState('popular');
  const [search, setSearch] = useState('');
  const [previewAsset, setPreviewAsset] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = useMemo(() => {
    let list = [...MARKETPLACE_ASSETS];
    if (activeCategory !== 'all') list = list?.filter((a) => a?.category === activeCategory);
    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter((a) => a?.title?.toLowerCase()?.includes(q) || a?.creator?.toLowerCase()?.includes(q) || a?.tags?.some((t) => t?.includes(q)));
    }
    if (sort === 'popular') list?.sort((a, b) => b?.sales - a?.sales);else
    if (sort === 'newest') list?.sort((a, b) => b?.id - a?.id);else
    if (sort === 'price-low') list?.sort((a, b) => a?.price - b?.price);else
    if (sort === 'price-high') list?.sort((a, b) => b?.price - a?.price);else
    if (sort === 'rating') list?.sort((a, b) => b?.rating - a?.rating);
    return list;
  }, [activeCategory, sort, search]);

  const featured = MARKETPLACE_ASSETS?.filter((a) => a?.featured)?.slice(0, 3);
  const totalAssets = MARKETPLACE_ASSETS?.length;
  const totalSales = MARKETPLACE_ASSETS?.reduce((acc, a) => acc + a?.sales, 0);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      {/* Toast */}
      {toast &&
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.95)', color: 'white', backdropFilter: 'blur(8px)' }}>
          <Icon name="CheckCircle2" size={16} color="white" />
          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>{toast}</span>
        </div>
      }
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>AI Asset Marketplace</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                {totalAssets} assets · {totalSales?.toLocaleString()} total sales · Buy, sell, and remix AI-generated assets
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/creator-dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                
                <Icon name="Upload" size={15} color="var(--color-muted-foreground)" />
                Sell Assets
              </button>
              <button
                onClick={() => navigate('/creator-dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}>
                
                <Icon name="Wand2" size={15} color="white" />
                Create
              </button>
            </div>
          </div>

          {/* Featured assets */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Star" size={18} color="#F59E0B" fill="#F59E0B" />
              <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Featured Assets</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featured?.map((asset) => {
                const catColor = CATEGORY_COLORS?.[asset?.category] || 'var(--color-primary)';
                const cat = CATEGORIES?.find((c) => c?.id === asset?.category);
                return (
                  <div
                    key={asset?.id}
                    className="relative rounded-2xl overflow-hidden cursor-pointer group"
                    style={{ height: 180 }}
                    onClick={() => setPreviewAsset(asset)}>
                    
                    <Image src={asset?.thumbnail} alt={asset?.thumbnailAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 50%)' }} />
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: catColor, color: 'white', fontFamily: 'var(--font-caption)' }}>{cat?.label}</span>
                      <h3 className="text-white font-bold mt-1 text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{asset?.title}</h3>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-white/70 text-xs" style={{ fontFamily: 'var(--font-caption)' }}>by {asset?.creator}</p>
                        <span className="text-white font-bold text-sm" style={{ fontFamily: 'var(--font-data)' }}>${asset?.price}</span>
                      </div>
                    </div>
                  </div>);

              })}
            </div>
          </section>

          {/* Filters + Search */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <div className="flex gap-1.5 overflow-x-auto pb-1 flex-1" style={{ scrollbarWidth: 'none' }}>
              {CATEGORIES?.map((cat) =>
              <button
                key={cat?.id}
                onClick={() => setActiveCategory(cat?.id)}
                className="flex-shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all"
                style={{
                  borderColor: activeCategory === cat?.id ? 'var(--color-primary)' : 'var(--color-border)',
                  background: activeCategory === cat?.id ? 'rgba(99,102,241,0.1)' : 'var(--color-card)',
                  color: activeCategory === cat?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-caption)'
                }}>
                
                  <Icon name={cat?.icon} size={11} color={activeCategory === cat?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                  {cat?.label}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  placeholder="Search assets..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="pl-8 pr-3 py-2 text-xs rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-44"
                  style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }} />
                
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e?.target?.value)}
                className="text-xs px-3 py-2 rounded-xl border focus:outline-none"
                style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)', fontFamily: 'var(--font-caption)' }}>
                
                {SORT_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
              </select>
            </div>
          </div>

          {/* Asset grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered?.map((asset) =>
            <AssetCard
              key={asset?.id}
              asset={asset}
              onBuy={(a) => showToast(`"${a?.title}" purchased successfully!`)}
              onPreview={setPreviewAsset} />

            )}
          </div>

          {filtered?.length === 0 &&
          <div className="flex flex-col items-center justify-center py-16 rounded-2xl border" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
              <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
              <p className="text-sm font-semibold mt-3" style={{ color: 'var(--color-foreground)' }}>No assets found</p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Try a different search or category</p>
            </div>
          }

          {/* Sell your assets CTA */}
          <div
            className="mt-8 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))', border: '1px solid rgba(99,102,241,0.2)' }}>
            
            <div>
              <h3 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Sell Your AI Creations</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Earn 85% of every sale. LoopNest handles payments and distribution.
              </p>
            </div>
            <button
              onClick={() => navigate('/creator-dashboard')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 flex-shrink-0"
              style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}>
              
              <Icon name="DollarSign" size={15} color="white" />
              Start Selling
            </button>
          </div>
        </div>
      </main>
      {/* Preview modal */}
      {previewAsset &&
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
        onClick={(e) => e?.target === e?.currentTarget && setPreviewAsset(null)}>
        
          <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="relative h-48">
              <Image src={previewAsset?.thumbnail} alt={previewAsset?.thumbnailAlt} className="w-full h-full object-cover" />
              <button onClick={() => setPreviewAsset(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
                <Icon name="X" size={15} color="white" />
              </button>
            </div>
            <div className="p-4">
              <h3 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{previewAsset?.title}</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>by {previewAsset?.creator} · ⭐ {previewAsset?.rating} · {previewAsset?.sales} sales</p>
              <div className="flex gap-2">
                <button onClick={() => setPreviewAsset(null)} className="flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all" style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Close</button>
                <button
                onClick={() => {showToast(`"${previewAsset?.title}" purchased!`);setPreviewAsset(null);}}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                style={{ background: CATEGORY_COLORS?.[previewAsset?.category] || 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}>
                
                  Buy ${previewAsset?.price}
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

}